/**
 * Mise en forme des notes d'une entree (« Markdown » des notes de 1Password) : titres, gras, italique, barre,
 * code, listes (a puces, numerotees, cases a cocher, imbriquees), citations, filets, liens et tableaux.
 *
 * La note est une donnee d'utilisateur ET une donnee importee (un CSV, un export d'un autre gestionnaire) : elle se
 * lit comme une donnee NON FIABLE. Le rendu produit du HTML ou CHAQUE caractere de la note passe par `escapeHtml` ;
 * seules les balises de la liste ci-dessus sont emises, jamais une balise ni un attribut venus de la note -- du HTML
 * tape dans une note s'affiche comme du texte. Un lien ne s'ouvre que vers `http:` / `https:` (`openableUri`, la
 * garde des adresses de la fiche), dans un nouvel onglet sans `opener` ni referent ; `javascript:`, `data:`... restent
 * du texte. Une image (`![texte](adresse)`) n'est JAMAIS chargee : ouvrir une note ne doit rien envoyer a un tiers
 * (pixel espion) ; elle devient un lien vers l'adresse, sous son texte.
 *
 * Une note porte souvent des codes de secours ou des cles (`ab_cd_ef`, `x*y*z`) : la mise en forme ne doit jamais
 * en manger un caractere. D'ou des regles plus prudentes que Markdown : aucune emphase (`*`, `_`, `~~`) ne s'ouvre ni
 * ne se ferme a l'interieur d'un mot (`x*y*z` reste `x*y*z`), une emphase exige un caractere non blanc juste apres
 * l'ouverture et juste avant la fermeture, et sans fermeture le delimiteur reste tel quel. Le texte brut reste toujours a un clic (bascule « Texte brut » de la fiche).
 */
import { escapeHtml } from '../ui/dom.js';
import { openableUri } from './entry-uris.js';

/** Preference de cet appareil (jamais un secret) : les notes s'affichent mises en forme, ou en texte brut. */
export type NotesFormat = 'markdown' | 'plain';

/** Cle `localStorage` : une preference de cet appareil, jamais un secret. */
export const NOTES_FORMAT_KEY = 'klavox:notes-format';

/** Relue comme une donnee non fiable : seul `plain` coupe la mise en forme, toute autre valeur la garde. */
export function notesFormat(raw: string | null): NotesFormat {
  return raw === 'plain' ? 'plain' : 'markdown';
}

/**
 * Apercu des notes dans l'editeur : le MEME rendu que la fiche (`renderNotesMarkdown`, meme echappement, meme garde
 * des liens, image jamais chargee), recalcule a la frappe. Une note vide ou faite de blancs dit qu'il n'y a rien a
 * apercevoir, plutot qu'un cadre vide.
 */
export function renderNotesPreview(text: string): string {
  return text.trim().length === 0 ? '<p class="notes-preview-empty">Rien a apercevoir : la note est vide.</p>' : renderNotesMarkdown(text);
}

/** Vrai si la note porte au moins une mise en forme reconnue (sinon la bascule « Texte brut » n'a rien a montrer). */
export function notesHaveMarkdown(text: string): boolean {
  return renderNotesMarkdown(text) !== plainParagraphs(text);
}

/** Le rendu d'une note sans aucune mise en forme : paragraphes et retours a la ligne, tout echappe. */
function plainParagraphs(text: string): string {
  return splitParagraphs(text.replace(/\r\n?/gu, '\n').split('\n'))
    .map((lines) => `<p>${lines.map((line) => escapeHtml(line.trim())).join('<br>')}</p>`)
    .join('');
}

function splitParagraphs(lines: readonly string[]): string[][] {
  const paragraphs: string[][] = [];
  let current: string[] = [];
  for (const line of lines) {
    if (line.trim().length === 0) {
      if (current.length > 0) paragraphs.push(current);
      current = [];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) paragraphs.push(current);
  return paragraphs;
}

const LINK_ATTRIBUTES = 'target="_blank" rel="noopener noreferrer"';
const ESCAPABLE = /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/u;
const FENCE = /^ {0,3}(`{3,}|~{3,})/u;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(\S.*?)(?:[ \t]+#+)?[ \t]*$/u;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/u;
const QUOTE = /^ {0,3}>[ \t]?/u;
const LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])[ \t]+(.*)$/u;
const TASK = /^\[([ xX])\][ \t]+(.*)$/u;
/** Cellule d'une ligne de separation de tableau : `---`, `:---`, `---:` ou `:---:`. */
const TABLE_DELIMITER_CELL = /^:?-+:?$/u;
/** Colonnes d'un tableau au plus : au-dela, la note reste du texte (un tableau forge ne doit pas gonfler la fiche). */
const MAX_TABLE_COLUMNS = 32;
/** Profondeur maximale des citations imbriquees : une note forgee de dix mille `>` ne doit pas epuiser la pile. */
const MAX_QUOTE_DEPTH = 8;

/** Rendu HTML d'une note : sur, a inserer tel quel dans la fiche. */
export function renderNotesMarkdown(text: string): string {
  return renderBlocks(text.replace(/\r\n?/gu, '\n').split('\n'), 0);
}

function renderBlocks(lines: readonly string[], depth: number): string {
  const out: string[] = [];
  let paragraph: string[] = [];
  const flush = (): void => {
    if (paragraph.length > 0) out.push(`<p>${paragraph.map((line) => renderInline(line.trim())).join('<br>')}</p>`);
    paragraph = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const fence = FENCE.exec(line);
    if (fence !== null) {
      flush();
      const marker = fence[1]!;
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !isClosingFence(lines[i]!, marker)) {
        body.push(lines[i]!);
        i += 1;
      }
      i += 1; // la fence de fermeture (ou la fin de la note)
      out.push(`<pre><code>${escapeHtml(body.join('\n'))}</code></pre>`);
      continue;
    }
    if (line.trim().length === 0) {
      flush();
      i += 1;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading !== null) {
      flush();
      // Dans la fiche, un titre de note reste sous le titre de l'entree (h2) : # -> h3, ## -> h4, le reste -> h5.
      const level = Math.min(heading[1]!.length + 2, 5);
      out.push(`<h${level}>${renderInline(heading[2]!)}</h${level}>`);
      i += 1;
      continue;
    }
    if (RULE.test(line)) {
      flush();
      out.push('<hr>');
      i += 1;
      continue;
    }
    if (QUOTE.test(line) && depth < MAX_QUOTE_DEPTH) {
      flush();
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        quoted.push(lines[i]!.replace(QUOTE, ''));
        i += 1;
      }
      out.push(`<blockquote>${renderBlocks(quoted, depth + 1)}</blockquote>`);
      continue;
    }
    if (LIST_ITEM.test(line)) {
      flush();
      const items: string[] = [];
      while (i < lines.length && LIST_ITEM.test(lines[i]!)) {
        items.push(lines[i]!);
        i += 1;
      }
      out.push(renderList(items));
      continue;
    }
    const table = tableAt(lines, i);
    if (table !== null) {
      flush();
      out.push(table.html);
      i = table.end;
      continue;
    }
    paragraph.push(line);
    i += 1;
  }
  flush();
  return out.join('');
}

/** Fermeture d'un bloc de code : le meme caractere, au moins aussi long que l'ouverture, seul sur sa ligne. */
function isClosingFence(line: string, marker: string): boolean {
  const close = /^ {0,3}(`{3,}|~{3,})[ \t]*$/u.exec(line);
  return close !== null && close[1]![0] === marker[0] && close[1]!.length >= marker.length;
}

type TableAlign = 'left' | 'center' | 'right' | null;

/**
 * Cellules d'une ligne de tableau, ou `null` si elle ne porte aucun `|` non echappe. Les `|` de bord sont
 * facultatifs ; `\|` est un `|` dans la cellule (meme dans du `code`, comme GitHub), jamais une separation.
 */
function tableCells(line: string): string[] | null {
  const text = line.trim();
  const cells: string[] = [];
  let cell = '';
  let pipes = 0;
  let endsWithPipe = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;
    endsWithPipe = false;
    if (char === '\\' && i + 1 < text.length) {
      cell += text[i + 1] === '|' ? '|' : char + text[i + 1]!;
      i += 1;
    } else if (char === '|') {
      pipes += 1;
      endsWithPipe = true;
      if (i > 0) cells.push(cell);
      cell = '';
    } else {
      cell += char;
    }
  }
  if (pipes === 0) return null;
  if (!endsWithPipe) cells.push(cell);
  return cells.map((value) => value.trim());
}

/** Alignements d'une ligne de separation (`| :--- | :---: | ---: |`), ou `null` si ce n'en est pas une. */
function tableAligns(line: string): TableAlign[] | null {
  const cells = tableCells(line);
  if (cells === null || cells.length === 0 || !cells.every((cell) => TABLE_DELIMITER_CELL.test(cell))) return null;
  return cells.map((cell) => {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    return left && right ? 'center' : right ? 'right' : left ? 'left' : null;
  });
}

/**
 * Une ligne qui ouvre un autre bloc termine le tableau, comme une ligne vide (GFM) : une liste, case ou liste
 * numerotee collee sous un tableau d'acces reste une liste, jamais des cellules.
 */
function startsBlock(line: string): boolean {
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || LIST_ITEM.test(line);
}

/**
 * Tableau ouvert en `start` : une ligne d'en-tete puis une ligne de separation de meme nombre de colonnes (GitHub).
 * Les lignes suivantes, jusqu'a une ligne vide ou un autre bloc, sont le corps : une ligne trop courte est completee
 * de cellules vides, une ligne trop longue perd ce qui depasse. Sinon `null` : la ligne reste du texte.
 */
function tableAt(lines: readonly string[], start: number): { readonly html: string; readonly end: number } | null {
  const header = tableCells(lines[start]!);
  const aligns = start + 1 < lines.length ? tableAligns(lines[start + 1]!) : null;
  if (header === null || aligns === null || header.length !== aligns.length || aligns.length > MAX_TABLE_COLUMNS) return null;
  const cell = (tag: 'th' | 'td', content: string, column: number): string => {
    const align = aligns[column];
    const attributes = (tag === 'th' ? ' scope="col"' : '') + (align === null || align === undefined ? '' : ` class="align-${align}"`);
    return `<${tag}${attributes}>${renderInline(content)}</${tag}>`;
  };
  const row = (tag: 'th' | 'td', cells: readonly string[]): string =>
    `<tr>${aligns.map((_, column) => cell(tag, cells[column] ?? '', column)).join('')}</tr>`;
  const body: string[] = [];
  let end = start + 2;
  while (end < lines.length && lines[end]!.trim().length > 0 && !startsBlock(lines[end]!)) {
    body.push(row('td', tableCells(lines[end]!) ?? [lines[end]!.trim()]));
    end += 1;
  }
  const tbody = body.length > 0 ? `<tbody>${body.join('')}</tbody>` : '';
  // Un tableau large defile dans son cadre, la fiche ne s'elargit pas.
  return { html: `<div class="notes-table"><table><thead>${row('th', header)}</thead>${tbody}</table></div>`, end };
}

interface ListLine {
  readonly indent: number;
  readonly ordered: boolean;
  readonly start: number;
  readonly content: string;
}

/** Largeur d'une indentation : une tabulation compte pour quatre espaces. */
function indentWidth(raw: string): number {
  return [...raw].reduce((width, char) => width + (char === '\t' ? 4 : 1), 0);
}

/** Listes consecutives, imbriquees par l'indentation (deux espaces de plus que la ligne parente au moins). */
function renderList(rawLines: readonly string[]): string {
  const lines: ListLine[] = rawLines.map((raw) => {
    const match = LIST_ITEM.exec(raw)!;
    const marker = match[2]!;
    const ordered = /\d/u.test(marker);
    return { indent: indentWidth(match[1]!), ordered, start: ordered ? Number.parseInt(marker, 10) : 1, content: match[3]! };
  });
  let index = 0;
  const build = (indent: number): string => {
    const first = lines[index]!;
    const tag = first.ordered ? 'ol' : 'ul';
    const start = first.ordered && first.start !== 1 ? ` start="${first.start}"` : '';
    const items: string[] = [];
    while (index < lines.length && lines[index]!.indent < indent + 2 && lines[index]!.ordered === first.ordered) {
      const line = lines[index]!;
      index += 1;
      let nested = '';
      while (index < lines.length && lines[index]!.indent >= line.indent + 2) nested += build(lines[index]!.indent);
      items.push(`<li>${renderListContent(line.content)}${nested}</li>`);
      if (index < lines.length && lines[index]!.indent < indent) break;
    }
    return `<${tag}${start}>${items.join('')}</${tag}>`;
  };
  let html = '';
  while (index < lines.length) html += build(lines[index]!.indent);
  return html;
}

function renderListContent(content: string): string {
  const task = TASK.exec(content);
  if (task === null) return renderInline(content);
  const done = task[1] !== ' ';
  return `<input type="checkbox" disabled${done ? ' checked' : ''} aria-label="${done ? 'Fait' : 'A faire'}"> ${renderInline(task[2]!)}`;
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && /[\p{L}\p{N}]/u.test(char);
}

function isBlank(char: string | undefined): boolean {
  return char === undefined || /\s/u.test(char);
}

/** Emphases reconnues, de la plus longue a la plus courte : `**` passe avant `*`. */
const EMPHASES: readonly { readonly delimiter: string; readonly tag: string }[] = [
  { delimiter: '**', tag: 'strong' },
  { delimiter: '__', tag: 'strong' },
  { delimiter: '~~', tag: 'del' },
  { delimiter: '*', tag: 'em' },
  { delimiter: '_', tag: 'em' },
];

/**
 * Fermeture d'une emphase ouverte en `from` : le meme delimiteur, precede d'un non-blanc et non suivi d'une lettre
 * ou d'un chiffre (jamais au milieu d'un mot). Un `*` simple ne ferme pas sur un `**`.
 */
function closingIndex(text: string, delimiter: string, from: number): number {
  let at = text.indexOf(delimiter, from);
  while (at !== -1) {
    const before = text[at - 1];
    const after = text[at + delimiter.length];
    const intraword = isWordChar(after);
    const doubled = delimiter.length === 1 && (after === delimiter || before === delimiter);
    if (at > from && !isBlank(before) && !intraword && !doubled && text[at - 1] !== '\\') return at;
    at = text.indexOf(delimiter, at + 1);
  }
  return -1;
}

/** Fin d'un `[texte]` ouvert en `open` (crochets equilibres), ou -1. */
function closingBracket(text: string, open: number): number {
  let depth = 0;
  for (let i = open; i < text.length; i += 1) {
    if (text[i] === '\\') i += 1;
    else if (text[i] === '[') depth += 1;
    else if (text[i] === ']') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function link(href: string, label: string): string {
  return `<a href="${escapeHtml(href)}" ${LINK_ATTRIBUTES}>${label}</a>`;
}

/**
 * Une ligne : echappements, code, liens, adresses nues, emphases ; tout le reste est du texte echappe. Dans le texte
 * d'un lien (`inLink`), ni lien ni adresse nue : jamais de lien dans un lien.
 */
function renderInline(text: string, inLink = false): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const char = text[i]!;

    if (char === '\\' && ESCAPABLE.test(text[i + 1] ?? '')) {
      out += escapeHtml(text[i + 1]!);
      i += 2;
      continue;
    }

    if (char === '`') {
      const run = /^`+/u.exec(text.slice(i))![0];
      let close = text.indexOf(run, i + run.length);
      while (close !== -1 && text[close + run.length] === '`') close = text.indexOf(run, close + run.length + 1);
      if (close !== -1) {
        const code = text.slice(i + run.length, close);
        out += `<code>${escapeHtml(code.length > 2 && code.startsWith(' ') && code.endsWith(' ') ? code.slice(1, -1) : code)}</code>`;
        i = close + run.length;
        continue;
      }
      out += escapeHtml(run);
      i += run.length;
      continue;
    }

    const image = char === '!' && text[i + 1] === '[';
    if (!inLink && (char === '[' || image)) {
      const open = image ? i + 1 : i;
      const close = closingBracket(text, open);
      const target = close === -1 ? null : /^\(([^\s()]+)\)/u.exec(text.slice(close + 1));
      const href = target === null || target === undefined ? null : openableUri(target[1]!);
      if (target !== null && target !== undefined && href !== null) {
        const label = text.slice(open + 1, close);
        out += link(href, image ? escapeHtml(label.length > 0 ? label : href) : renderInline(label, true));
        i = close + 1 + target[0].length;
        continue;
      }
    }

    if (!inLink && (char === 'h' || char === 'H') && !isWordChar(text[i - 1])) {
      const bare = /^https?:\/\/[^\s<>"]+/iu.exec(text.slice(i));
      if (bare !== null) {
        // La ponctuation qui clot une phrase n'appartient pas a l'adresse (« voir https://exemple.fr. »).
        const url = bare[0].replace(/[.,;:!?'")\]]+$/u, '');
        const href = openableUri(url);
        if (href !== null) {
          out += link(href, escapeHtml(url));
          i += url.length;
          continue;
        }
      }
    }

    const emphasis = EMPHASES.find(({ delimiter }) => text.startsWith(delimiter, i));
    if (emphasis !== undefined) {
      const { delimiter, tag } = emphasis;
      const start = i + delimiter.length;
      const opens =
        !isBlank(text[start]) &&
        !isWordChar(text[i - 1]) &&
        !(delimiter.length === 1 && text[start] === delimiter);
      const close = opens ? closingIndex(text, delimiter, start) : -1;
      if (close !== -1) {
        out += `<${tag}>${renderInline(text.slice(start, close), inLink)}</${tag}>`;
        i = close + delimiter.length;
        continue;
      }
      out += escapeHtml(delimiter);
      i += delimiter.length;
      continue;
    }

    out += escapeHtml(char);
    i += 1;
  }
  return out;
}
